import type { PartLine, ScoreSummary } from '@corocanto/scoring';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { Member, Song } from './types';

export const songsKey = (choirId: string) => ['songs', choirId] as const;

export function useSongs(choirId: string | undefined) {
  return useQuery({
    queryKey: songsKey(choirId ?? ''),
    queryFn: () => api<Song[]>(`/api/choirs/${choirId}/songs`),
    enabled: !!choirId,
  });
}

export function useSong(choirId: string | undefined, songId: string | undefined) {
  return useQuery({
    queryKey: ['song', choirId, songId],
    queryFn: () => api<Song>(`/api/choirs/${choirId}/songs/${songId}`),
    enabled: !!choirId && !!songId,
  });
}

/** The query for a song's original MusicXML upload (bytes); shared so a page can load several ahead. */
export function songFileQuery(choirId: string | undefined, songId: string | undefined) {
  return {
    queryKey: ['songFile', choirId, songId],
    queryFn: async () => {
      const response = await fetch(`/api/choirs/${choirId}/songs/${songId}/file`, { credentials: 'same-origin' });
      if (!response.ok) throw new ApiError(response.status, undefined, response.statusText);
      return new Uint8Array(await response.arrayBuffer());
    },
    enabled: !!choirId && !!songId,
    staleTime: Infinity,
  };
}

/** The original MusicXML upload (bytes), as stored. */
export function useSongFile(choirId: string | undefined, songId: string | undefined) {
  return useQuery(songFileQuery(choirId, songId));
}

const myLineKey = (choirId: string | undefined, songId: string | undefined) => ['myLine', choirId, songId] as const;

/** The line the signed-in member sings in a song where their part divides (1 or 2), or null when not chosen yet. */
export function useMyLine(choirId: string | undefined, songId: string | undefined) {
  return useQuery({
    queryKey: myLineKey(choirId, songId),
    queryFn: () => api<{ line: PartLine | null }>(`/api/choirs/${choirId}/songs/${songId}/my-line`).then((r) => r.line),
    enabled: !!choirId && !!songId,
  });
}

/** Remember the line for this member and song, on every computer. */
export function useSetMyLine(choirId: string | undefined, songId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (line: PartLine) =>
      api<{ line: PartLine }>(`/api/choirs/${choirId}/songs/${songId}/my-line`, { method: 'PUT', json: { line } }),
    onSuccess: (r) => queryClient.setQueryData(myLineKey(choirId, songId), r.line),
  });
}

export interface SongUpload {
  file: File;
  summary: ScoreSummary;
  title: string;
  composer: string;
}

export function useUploadSong(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, summary, title, composer }: SongUpload) => {
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('summary', JSON.stringify(summary));
      form.append('title', title.trim());
      if (composer.trim()) form.append('composer', composer.trim());
      return api<Song>(`/api/choirs/${choirId}/songs`, { method: 'POST', form });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: songsKey(choirId) }),
  });
}

export function useMembers(choirId: string | undefined) {
  return useQuery({
    queryKey: ['members', choirId],
    queryFn: () => api<Member[]>(`/api/choirs/${choirId}/members`),
    enabled: !!choirId,
  });
}

/** Rename a song (director): title and composer; a blank composer clears it. */
export function useRenameSong(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ songId, title, composer }: { songId: string; title: string; composer: string }) =>
      api<Song>(`/api/choirs/${choirId}/songs/${songId}`, { method: 'PATCH', json: { title: title.trim(), composer: composer.trim() } }),
    onSuccess: (song) => {
      queryClient.setQueryData(['song', choirId, song.id], song);
      void queryClient.invalidateQueries({ queryKey: songsKey(choirId) });
    },
  });
}

/** Delete a song (director) with its submitted takes, grades, comments and recordings. */
export function useDeleteSong(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (songId: string) => api<void>(`/api/choirs/${choirId}/songs/${songId}`, { method: 'DELETE' }),
    onSuccess: (_, songId) => {
      queryClient.removeQueries({ queryKey: ['song', choirId, songId] });
      queryClient.removeQueries({ queryKey: ['songFile', choirId, songId] });
      void queryClient.invalidateQueries({ queryKey: songsKey(choirId) });
      void queryClient.invalidateQueries({ queryKey: ['review', choirId] });
    },
  });
}
